import { Injectable } from '@angular/core';
import { catchError, forkJoin, Observable, of } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';
import { GetSocialListeningCompetitorProjectsService } from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/get-social-listening-competitor-projects.service';
import { GetSocialListeningIndustryListService } from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/get-social-listening-industry-list.service';
import {
	BrandListItem,
	GetSocialListeningBrandListService
} from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/get-social-listening-brand-list.service';

export interface SlEntitiesState {
	hasBrand: boolean;
	hasCompetitor: boolean;
	hasIndustry: boolean;
}

export interface SlSubmenuItem {
	title: string;
	icon: string;
	path: string;
	disabled?: boolean;
	tooltip?: string;
	/**
	 * Every route that IS this item, for the rail's "you are here" marker.
	 *
	 * Not derivable from `path`, and guessing it was wrong twice. `path` is
	 * where the item SENDS you, and the redirect guards send an account with no
	 * entity yet to a setup page whose name shares nothing with it — Brands goes
	 * to `my-brand` and lands on `brand-setup`. And an item may point at one
	 * page of a section whose other pages are just as much its own: Reports
	 * points at `reports/info`, and `reports/create` is still Reports.
	 *
	 * Matched as path-segment prefixes. A new setup route or a new page outside
	 * an item's own subtree has to be listed here, or it will be a page the
	 * rail cannot say you are on.
	 */
	matches: string[];
}

const SL_BASE = '/social-listening';
const ENTITIES_TTL_MS = 45_000;

const EMPTY_STATE: SlEntitiesState = {
	hasBrand: false,
	hasCompetitor: false,
	hasIndustry: false
};

/**
 * Central brain for the Social Listening sidebar item:
 * - knows which SL entities exist (cached),
 * - builds the submenu that click and hover both open.
 *
 * Keeps the shared sidebar component thin and SL-agnostic.
 *
 * It no longer resolves a "landing" route, because the item no longer
 * navigates: there is no combined setup wizard to send a new account to, so
 * both click and hover open the submenu and the user picks the entity. Each
 * submenu item points at its entity's landing route, whose redirect guard
 * resolves first-entity-vs-setup-wizard.
 */
@Injectable({ providedIn: 'root' })
export class SocialListeningNavService {
	private cache: { state: SlEntitiesState; at: number } | null = null;
	private inFlight$: Observable<SlEntitiesState> | null = null;

	constructor(
		private brandListService: GetSocialListeningBrandListService,
		private competitorProjectsService: GetSocialListeningCompetitorProjectsService,
		private industryListService: GetSocialListeningIndustryListService
	) {}

	/** Last known state without triggering a request (for instant flyout). */
	getCachedState(): SlEntitiesState | null {
		return this.cache?.state ?? null;
	}

	/** Drop the cache so the next load() re-fetches (call after creating an entity). */
	refresh(): void {
		this.cache = null;
		this.inFlight$ = null;
	}

	/**
	 * Resolve which SL entities the user has. Cached for ENTITIES_TTL_MS;
	 * concurrent calls share one in-flight request. Any API error is treated
	 * as "no entity" (never trusts mock data). Nothing routes off that answer
	 * any more — the submenu is the same seven items either way — so a wrong
	 * "no entity" costs nothing; it is kept because callers still ask.
	 */
	loadEntities(forceRefresh = false): Observable<SlEntitiesState> {
		const now = Date.now();
		if (
			!forceRefresh &&
			this.cache &&
			now - this.cache.at < ENTITIES_TTL_MS
		) {
			return of(this.cache.state);
		}
		if (this.inFlight$) {
			return this.inFlight$;
		}

		this.inFlight$ = forkJoin([
			this.brandList$(),
			this.competitorProjectsService.fetchAll(),
			this.industryListService.fetch()
		]).pipe(
			map(([brands, projects, industries]) => ({
				hasBrand: brands.length > 0,
				hasCompetitor: projects.length > 0,
				hasIndustry: industries.length > 0
			})),
			tap(state => {
				this.cache = { state, at: Date.now() };
				this.inFlight$ = null;
			}),
			catchError(() => {
				this.inFlight$ = null;
				return of(EMPTY_STATE);
			}),
			shareReplay(1)
		);

		return this.inFlight$;
	}

	/**
	 * The submenu — always all seven options, for click and hover alike. Each
	 * entity item points at its landing route; that route's redirect guard
	 * resolves first-entity-vs-setup (and retries the first-load race), so a
	 * stale "no entity" flag can never send the user straight to a setup
	 * wizard.
	 */
	buildSubmenu(_state: SlEntitiesState | null): SlSubmenuItem[] {
		return [
			{
				title: 'Brands',
				icon: 'members',
				// brandRedirectGuard handles the empty → brand-setup case.
				path: `${SL_BASE}/my-brand`,
				matches: [
					`${SL_BASE}/my-brand`,
					`${SL_BASE}/brand-setup`,
					// Still routed, and redirects to my-brand.
					`${SL_BASE}/general-setup`
				]
			},
			{
				title: 'Competitors',
				icon: 'members',
				// competitorProjectRedirectGuard handles empty → competitors-setup.
				path: `${SL_BASE}/competitors`,
				matches: [
					`${SL_BASE}/competitors`,
					`${SL_BASE}/competitors-setup`
				]
			},
			{
				title: 'Industry',
				icon: 'members',
				// industryRedirectGuard handles the empty → industry-setup case.
				path: `${SL_BASE}/industry`,
				matches: [`${SL_BASE}/industry`, `${SL_BASE}/industry-setup`]
			},
			{
				title: 'Find mentions',
				icon: 'search',
				path: `${SL_BASE}/find`,
				matches: [`${SL_BASE}/find`]
			},
			{
				title: 'Risk & Reputation',
				icon: 'nav_mentions_and_insights',
				// The section is closed behind a demo: every risk URL shows the locked page.
				path: `${SL_BASE}/risk`,
				matches: [`${SL_BASE}/risk`, `${SL_BASE}/risk-setup`]
			},
			{
				title: 'Reports',
				icon: 'nav_mentions_and_insights',
				path: `${SL_BASE}/reports/info`,
				// The whole section, not the page the item opens on.
				matches: [`${SL_BASE}/reports`]
			},
			{
				title: 'AI Summary',
				icon: 'nav_ai_agent',
				path: `${SL_BASE}/ai-summary`,
				matches: [`${SL_BASE}/ai-summary`]
			}
		];
	}

	/**
	 * The brand list, from the one service every page uses. It had its own
	 * copy here only because that service answered a failure with three
	 * invented brands, which an existence check cannot take; the service
	 * retries and returns none now, which is what this copy did.
	 */
	private brandList$(): Observable<BrandListItem[]> {
		return this.brandListService.fetch();
	}
}
